import { Component, Inject, OnInit, OnDestroy, PLATFORM_ID, Renderer2, ChangeDetectorRef, ViewChild, ComponentRef, AfterViewInit, Injector } from '@angular/core';
import { Router, NavigationEnd, NavigationStart } from '@angular/router';
import { AuthenticationService } from './_services';
import { DOCUMENT, isPlatformBrowser, isPlatformServer, Location } from '@angular/common';
import { Angulartics2Facebook, Angulartics2Mixpanel } from 'angulartics2';
import { Angulartics2GoogleGlobalSiteTag } from 'angulartics2';
import { environment } from 'src/environments/environment';
import { AnalyticsService } from './_services/analytics.service';
import { ConsentService } from './_services/consent.service';
import { LanguageService } from './_services/language.service';
import { BreakpointObserver, MediaMatcher } from '@angular/cdk/layout';
import { MaterialContent } from './components/page/material-content.component';
import { MatSidenav } from '@angular/material/sidenav';
import { CdkPortalOutletAttachedRef } from '@angular/cdk/portal';
import { SidenavService } from 'src/app/_services/sidenav.service';
import { BotDetectionService } from './_services/bot-detection.service';
import { ScrollService } from './_services/scroll.service';
import { IS_MOBILE_AGENT } from './tokens';

interface ExtendedMixpanel extends Mixpanel {
  __SV?: number;
  _i?: any[];
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent extends MaterialContent implements OnInit, OnDestroy, AfterViewInit {
  
  title = 'Listx';
  isWebView: boolean = false;
  
  phoneQuery: MediaQueryList | undefined;
  
  private phoneQueryListener: () => void;
  private isMobileAgent: boolean | undefined;
  
  showConsent: boolean = false;

  @ViewChild(MatSidenav) sidenav: MatSidenav;
    
  constructor(
    private authenticationService: AuthenticationService,
    private router: Router,
    private angularticsMixpanel: Angulartics2Mixpanel,
    private angularticsGTag: Angulartics2GoogleGlobalSiteTag,
    private angularticsFacebook: Angulartics2Facebook,
    private consentService: ConsentService,
    private location: Location,
    private analyticsService: AnalyticsService,
    private changeDetectorRef: ChangeDetectorRef, 
    breakpointObserver: BreakpointObserver,
    public sidenavService: SidenavService,
    private botDetectionService: BotDetectionService,
    media: MediaMatcher,
    @Inject(PLATFORM_ID) private platformId: any,
    @Inject(DOCUMENT) private document: any,
    private renderer: Renderer2,
    private scrollService: ScrollService,
    injector: Injector
  ) {
    
    super(breakpointObserver, true)
    
    try {
      this.isMobileAgent = injector.get(IS_MOBILE_AGENT);  
      console.log("Mobile agent detected: " + this.isMobileAgent);
    } catch (e) {
      console.log("Didn't determine whether agent is mobile or not");
    }
    
    if (isPlatformBrowser(this.platformId)) {
      this.phoneQuery = media.matchMedia('(max-width: 1007px)');
      this.phoneQueryListener = () => this.onPhoneQueryChange();
      this.phoneQuery.addListener(this.phoneQueryListener);
    }
    
    this.router.events.subscribe(event => {
      
      if (event instanceof NavigationStart) {
        this.scrollService.savePosition(this.router.url, window.scrollY);
        window.scrollTo(0, 0); // Scroll to top before the old page is destroyed
      }
    });
  }
  
  ngOnInit(): void {
    
    if (isPlatformBrowser(this.platformId)) {
      this.authenticationService.tryLogIn();  
      this.injectMixpanel();
      this.injectGTag();
      this.injectFacebookPixel();
      this.analyticsService.initAnalytics();
      
      if (environment.mixpanelEnabled) {
        this.angularticsMixpanel.startTracking();
      }

      this.angularticsGTag.startTracking();  
      this.angularticsFacebook.startTracking();
      this.injectTidio();
    } else {
      this.replaceAlternateLinks();
    }

    if (this.needToShowConsent()) {
      this.showConsent = true;
    }
  }
  
  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      document.body.removeAttribute('angular-loading')
      this.toggleSidenav(!this.phoneQuery!.matches);
    }
  }
  
  isMobile(): boolean {
    
    if (isPlatformServer(this.platformId)) {
      return this.isMobileAgent!!;
    }
    
    return this.phoneQuery!! && this.phoneQuery.matches;
  }
  
  private onPhoneQueryChange(): void {
    
    this.changeDetectorRef.detectChanges();
    this.toggleSidenav(!this.phoneQuery?.matches);
  }

  private toggleSidenav(open: boolean) {
    
    console.log("Toggle sidenav open=" + open);
    
    if (open) {
      this.sidenav.open();
    } else {
      this.sidenav.close();
    }
  }
    
  private needToShowConsent(): boolean {
    return !this.botDetectionService.isAnyBot() &&
        this.consentService.getConsentState() === null
  }
  
  ngOnDestroy(): void {
    if (this.phoneQuery) {
      this.phoneQuery.removeListener(this.phoneQueryListener);
    }
  }
  
  onPageSpecificNavigationPortalsAttached(ref: CdkPortalOutletAttachedRef | null) {
    if (ref instanceof ComponentRef) {
      ref.instance.onNavigationCompleted.subscribe(() => {
        if (this.phoneQuery && this.phoneQuery.matches) {
          this.sidenav.close();
        }
      });
    }
  }
  
  private stubMixpanel() {
    
    (function (f, b: any) { 
        if (!b.__SV) { 
            var e, g, i, h; 
            window.mixpanel = b; 
            b._i = []; 
            b.init = function (e: any, f: any, c: any) {
                 
                function g(a: any, d: any) { 
                    var b = d.split("."); 
                    2 == b.length && ((a = a[b[0]]), (d = b[1])); 
                    a[d] = function () { 
                        a.push([d].concat(Array.prototype.slice.call(arguments, 0))); 
                    }; 
                }
                 
                var a = b; 
                
                "undefined" !== typeof c ? (a = b[c] = []) : (c = "mixpanel");
                a.people = a.people || [];
                a.toString = function (a: any) { 
                  var d = "mixpanel"; 
                  "mixpanel" !== c && (d += "." + c);
                  a || (d += " (stub)");
                  return d; 
                }; 
                 
                 a.people.toString = function () { 
                    return a.toString(1) + ".people (stub)"; 
                 }; 
                 
                 i = "disable time_event track track_pageview track_links track_forms track_with_groups add_group set_group remove_group register register_once alias unregister identify name_tag set_config reset opt_in_tracking opt_out_tracking has_opted_in_tracking has_opted_out_tracking clear_opt_in_out_tracking start_batch_senders people.set people.set_once people.unset people.increment people.append people.union people.track_charge people.clear_charges people.delete_user people.remove".split( " ");
                 for (h = 0; h < i.length; h++) g(a, i[h]); 
                 var j = "set set_once union unset remove delete".split(" "); 
                 a.get_group = function () { 
                    function b(c: any) { 
                        d[c] = function () { 
                            var call2_args = arguments; 
                            var call2 = [c].concat(Array.prototype.slice.call(call2_args, 0)); a.push([e, call2]); 
                        }; 
                    } 
                    for ( var d: any = {}, e = ["get_group"].concat( Array.prototype.slice.call(arguments, 0)), c = 0; c < j.length; c++) 
                        b(j[c]); 
                    
                    return d; 
                 }; 
                 b._i.push([e, f, c]); 
             }; 
             b.__SV = 1.2; 
           } 
       })(document, window.mixpanel || []);
  }
    
  private injectMixpanel() {
    
    if (!environment.mixpanelEnabled) {
      console.log("Mixpanel disabled");
      return;  
    }
    
    this.stubMixpanel();

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = 'https://cdn.mxpnl.com/libs/mixpanel-2-latest.min.js';
    
    this.renderer.appendChild(this.document.body, script);
  }
  
  private injectGTag() {
    let script = this.renderer.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js`;

    this.renderer.appendChild(this.document.body, script);
  }
  
  private stubFacebookPixel() {
    
    if ((window as any)["fbq"]) return;
    
    let n: any = (window as any)["fbq"] = function() {
        n.callMethod ?
        n.callMethod.apply(n, arguments) : n.queue.push(arguments)
    };
        
    if (!(window as any)["_fbq"]) {
      (window as any)["_fbq"] = n;
    }
    
    n.push = n; 
    n.loaded = !0; 
    n.version = '2.0';
    n.queue = []; 
  }
  
  private injectFacebookPixel() {
    
    this.stubFacebookPixel();
    
    let script = this.renderer.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = `https://connect.facebook.net/en_US/fbevents.js`;

    this.renderer.appendChild(this.document.body, script);
  }
  
  private injectTidio() {
    let script = this.renderer.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = `//code.tidio.co/${environment.tidio.id}.js`;

    this.renderer.appendChild(this.document.body, script);
  }
  
  private replaceAlternateLinks() {
    this.removeAlternateLinks();
    this.addAlternateLinks();
  }
  
  private removeAlternateLinks() {
    const existingLinks = this.document.querySelectorAll('link[rel="alternate"]');
    existingLinks.forEach((link: any) => {
      this.renderer.removeChild(this.document.head, link);
    });
  }
  
  isAuthenticated(): boolean {
    return this.authenticationService.isAuthenticated();
  }
  
  async navigate(path: string) {
    this.router.navigate([path]);
    if (this.phoneQuery && this.phoneQuery.matches) {
      this.sidenav.close();
    }
  }
  
  private addAlternateLinks() {
    console.log(this.location.path())
    LanguageService.SUPPORTED_LOCALES.forEach(locale => {
      
      const localeSpecificURL = `${environment.baseURL}/${locale}${this.location.path()}`;
      this.addAlternateLink(locale, localeSpecificURL);
      const localeParts = locale.split("-");
      if (localeParts.length == 2) {
        this.addAlternateLink(localeParts[0], localeSpecificURL);
      }
    })
    
    this.addAlternateLink('x-default', `${environment.baseURL}${this.location.path()}`);
  }
  
  private addAlternateLink(language: string, url: string) {
    const link = this.renderer.createElement('link');
    this.renderer.setAttribute(link, 'rel', 'alternate');
    this.renderer.setAttribute(link, 'hreflang', language);
    this.renderer.setAttribute(link, 'href', url);
    this.renderer.appendChild(this.document.head, link);
  }
}
